import React, { useEffect, useRef } from "react";
import Layout from "../../components/layout/layout";
import { CrossHairIcon } from "../../assets/images/svgExports";
import Giving1 from "../../assets/images/giving1.png";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

type CoreValue = {
  id: string;
  title: string;
  description: string;
};

type JourneyMilestone = {
  year: string;
  title: string;
  description: string;
};

type GivingStat = {
  value: string;
  label: string;
};

const CORE_VALUES: CoreValue[] = [
  {
    id: "01",
    title: "Innovation",
    description:
      "We consistently explore cutting-edge technologies and creative approaches to deliver next-generation consumer engagement solutions.",
  },
  {
    id: "02",
    title: "Collaboration",
    description:
      "We believe in strategic partnerships—working with global brands and African telcos to create ecosystem-wide value.",
  },
  {
    id: "03",
    title: "Data-Driven",
    description: "Every campaign is backed by analytics and market research.",
  },
  {
    id: "04",
    title: "Cultural Relevance",
    description:
      "We celebrate Africa’s diverse communities, infusing authenticity into every project.",
  },
];

const JOURNEY: JourneyMilestone[] = [
  {
    year: "2010",
    title: "Genesis",
    description:
      "Launched our first tech-powered consumer engagement campaigns across West African markets, establishing foundational telco partnerships.",
  },
  {
    year: "2015",
    title: "VAS Licensing",
    description:
      "Secured official VAS provider licensing and expanded carrier integrations with MTN and major African MNOs, enabling direct billing and premium services.",
  },
  {
    year: "2020",
    title: "Platform Evolution",
    description:
      "Deployed scalable API-first infrastructure and ML-powered personalization engines, transforming how brands activate and retain consumers across Africa.",
  },
  {
    year: "Today",
    title: "Market Leadership",
    description:
      "Powering high-impact initiatives and processing millions of consumer interactions daily, setting new benchmarks for retention-driven growth.",
  },
];

const GIVING_STATS: GivingStat[] = [
  {
    value: "50+",
    label: "Educational Programs Supported",
  },
  {
    value: "10,000+",
    label: "Youth Reached",
  },
  {
    value: "15",
    label: "Communities Impacted",
  },
];

const LoyaltyPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // HERO INTRO ANIMATION (on load)
      const heroTl = gsap.timeline({
        defaults: { ease: "power3.out", duration: 0.7 },
      });

      heroTl
        .from(".loyalty-hero-heading", {
          y: 24,
          opacity: 0,
        })
        .from(
          ".loyalty-hero-mission",
          {
            y: 28,
            opacity: 0,
          },
          "-=0.4",
        );

      gsap.from(".loyalty-values-card", {
        y: 30,
        opacity: 0,
        duration: 0.7,
        ease: "power3.out",
        stagger: 0.12,
        scrollTrigger: {
          trigger: ".loyalty-values-section",
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
      });

      gsap.from(".loyalty-journey", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".loyalty-journey",
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // GIVING BACK – stats & images
      gsap.from(".loyalty-giving", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".loyalty-giving",
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      gsap.from(".loyalty-giving-stat", {
        x: -20,
        opacity: 0,
        duration: 0.6,
        ease: "power3.out",
        stagger: 0.12,
        scrollTrigger: {
          trigger: ".loyalty-giving",
          start: "top 70%",
          toggleActions: "play none none reverse",
        },
      });

      gsap.from(".loyalty-cta", {
        y: 40,
        scale: 0.98,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".loyalty-cta",
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <Layout
      footerData={{
        headerText: "Ready to Transform Your Consumer Engagement?",
        bodyText:
          "Join leading brands leveraging our platform to drive retention, loyalty, and lifetime value across African markets.",
      }}
    >
      <div ref={pageRef} className="bg-white">
        {/* HERO + MISSION */}
        <section
          className="
            relative bg-[#2C73DB] mb-[370px] md:mb-[350px] 
            rounded-bl-[80px] rounded-br-[80px]
            md:rounded-bl-[160px] md:rounded-br-[160px]
            lg:rounded-bl-[200px] lg:rounded-br-[200px]
            text-white pt-32 flex items-end justify-center
          "
        >
          <div className="max-w-5xl relative px-4 sm:px-6">
            <div className="loyalty-hero-heading text-center mb-[12%]">
              <div className="inline-flex items-center bg-[#ffffff33] text-xs font-[300] py-2 px-3 rounded-full mb-3 border border-[#fff] shadow-[0_0_18px_rgba(59,130,246,0.25)]">
                About Us
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-medium leading-tight mb-4 px-2">
                Powering Consumer Loyalty Across Africa{" "}
              </h1>

              <p className="text-sm md:text-base text-blue-100 max-w-2xl mx-auto leading-relaxed">
                A licensed VAS provider and retention-tech platform helping
                brands drive engagement, lifetime value, and sustainable growth
                in Africa&apos;s dynamic consumer markets.
              </p>
            </div>
          </div>

          {/* Mission card overlapping the hero bottom */}
          <div
            className="
              loyalty-hero-mission
              absolute left-1/2 -translate-x-1/2
              top-[92%]
              w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] max-w-3xl
            "
          >
            <div className="bg-blue-50 rounded-[60px] px-5 md:px-4 py-16">
              <div className="flex flex-col items-center text-center gap-4 md:gap-5">
                <div>
                  <div className="h-10 flex items-center justify-center w-full mb-6">
                    <CrossHairIcon />
                  </div>
                  <div>
                    <h2 className="text-lg md:text-3xl  font-medium mb-2 text-[#1E1E1E]">
                      Our Mission
                    </h2>

                    <p className="text-xs md:text-sm text-neutral-500 max-w-6xl mx-auto leading-relaxed">
                      Our mission is simple: help brands thrive in Africa&apos;s
                      vibrant consumer markets by leveraging data-driven
                      engagement technology, authentic cultural insights, and
                      strategic telecom partnerships
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* MAIN CONTENT */}
        <main className=" pb-20 md:pb-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-20 md:space-y-24">
            {/* CORE VALUES */}
            <section className="loyalty-values-section">
              <div className="text-center mb-10">
                <h2 className="text-xl text-[#1E1E1E] md:text-3xl  font-medium mb-2">
                  Core Values
                </h2>
                <p className="text-sm md:text-base text-neutral-500 max-w-xl mx-auto leading-relaxed">
                  The principles that drive our platform engineering and
                  customer success
                </p>
              </div>

              <div className="grid grid-cols-1 max-w-4xl mx-auto sm:grid-cols-2 gap-5 md:gap-6">
                {CORE_VALUES.map((value) => (
                  <article
                    key={value.id}
                    className="loyalty-values-card  bg-blue-50 rounded-[20px] px-6 py-6 md:px-7 md:py-8 shadow-sm"
                  >
                    <div className="flex flex-col items-start">
                      <span className="text-3xl md:text-5xl font-[700] text-blue-600 uppercase">
                        {value.id}
                      </span>
                      <p className="mt-6 text-xl md:text-2xl font-bold text-[#1e1e1e]">
                        {value.title}:
                      </p>
                    </div>
                    <p className="mt-2 text-xs md:text-sm text-[#8e8e8e] leading-relaxed">
                      {value.description}
                    </p>
                  </article>
                ))}
              </div>
            </section>

            {/* OUR JOURNEY */}
            <section className="loyalty-journey">
              <div className="bg-[#2C73DB] rounded-[60px] px-5 sm:px-6 md:px-12 py-8 md:py-16 text-white max-w-6xl shadow-md">
                <div className="text-center mb-8">
                  <h2 className="text-xl md:text-2xl font-semibold mb-2">
                    Our Journey
                  </h2>
                  <p className="text-xs md:text-sm text-[#fff] max-w-xl mx-auto leading-relaxed capitalize">
                    From early-stage campaigns to enterprise-grade engagement
                    infrastructure
                  </p>
                </div>

                <div className="relative mt-2">
                  {/* single continuous horizontal line */}
                  <div className="hidden md:block absolute top-[34px] left-0 right-0 h-px bg-blue-300/70" />

                  {/* milestones */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-10">
                    {JOURNEY.map((milestone) => (
                      <div
                        key={milestone.year}
                        className="flex flex-col items-start text-left"
                      >
                        <p className="text-sm ml-0 md:ml-2 md:text-base font-semibold mb-3">
                          {milestone.year}
                        </p>

                        {/* circle + vertical connector, sitting on the global line */}
                        <div className="relative mb-4">
                          <div className="w-4.5 h-4.5 -mt-2 flex items-center p-0.5 justify-center rounded-full border-[2px] border-gray-900 bg-blue-600 relative z-10">
                            <span className="w-3 h-3 bg-black rounded-full" />
                          </div>
                          <div className="absolute left-1/2 -translate-x-1/2 w-px h-6 bg-blue-200/80 mt-2">
                            <span
                              className="w-1.5 h-1.5
                              left-1/2 -translate-x-1/2 
                              bg-gray-50 rounded-full absolute bottom-0
                            "
                            />
                          </div>
                        </div>

                        <p className="text-xs md:text-sm mt-4 md:mt-6 font-semibold mb-1 ">
                          {milestone.title}
                        </p>
                        <p className="text-xs text-[#fff] leading-relaxed max-w-xs font-[200]">
                          {milestone.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* GIVING BACK */}
            <section className="flex max-w-4xl  mx-auto gap-8 md:flex-row flex-col-reverse">
              <div className="md:w-[50%]">
                <h2 className="text-2xl text-center md:text-left md:text-3xl font-semibold text-[#1e1e1e] mb-3 ">
                  Giving Back
                </h2>
                <p className="text-sm md:text-base text-[#8e8e8e] mb-8 leading-relaxed max-w-lg">
                  Community is at our core. We regularly support creative and
                  educational initiatives aimed at empowering Africa&apos;s
                  youth and fostering digital literacy.
                </p>

                <div className="space-y-4 w-full">
                  {GIVING_STATS.map((stat) => (
                    <div
                      key={stat.label}
                      className="
                        loyalty-giving-stat
                        bg-[#2C73DB] text-white rounded-2xl px-5 py-6
                        flex items-center gap-6 md:gap-10 shadow-md 
                      "
                    >
                      <h3 className="text-xl md:text-3xl font-bold">
                        {stat.value}
                      </h3>
                      <p className="text-xs md:text-sm text-blue-100">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex h-full  relative flex-col gap-4 items-start w-full lg:w-1/2">
                <img
                  src={Giving1}
                  alt="Giving Back  2"
                  className="w-full h-full object-contain"
                />
              </div>
            </section>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default LoyaltyPage;
